import { NgModule } from '@angular/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule, Routes } from '@angular/router';

import { AppComponent } from './app.component';
import { ApplicationCoreModule } from './core/core.module';
import { HeaderModule } from './header/header.module';
import { TopBarModule } from './top-bar/top-bar.module';
import { MenuBarModule } from './menu-bar/menu-bar.module';
import { FooterModule } from "./footer/footer.module";
import { LoginComponent } from './routes/login/login.component';
import { LoginModule } from './routes/login/login.module';
import { enGB } from 'date-fns/locale';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { GeonetworkToolComponent } from './routes/external-tool/geonetwork-tool.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ModellerGuard } from './core/auth/modeller.guard';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthGuard } from './core/auth/auth.guard';
import { GroupListAdminComponent } from './routes/group-list/group-list.component';
import { UserListAdminComponent } from './routes/user-list/user-list.component';
import { AdminGuard } from './core/auth/admin.guard';
import { AccessDeniedComponent } from './routes/access-denied/access-denied.component';
import { NotFoundComponent } from './routes/not-found/not-found.component';
import { ImportModule } from './routes/import/import.module';
import { ImportComponent } from './routes/import/import.component';
import { ExportComponent } from './routes/export/export.component';
import { ExportModule } from './routes/export/export.module';


const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'dashboard',
    loadChildren: () => import('./routes/dashboard/dashboard.module').then(m => m.DashboardModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'tool',
    loadChildren: () => import('./routes/external-tool/external-tool.module').then(m => m.ExternalToolModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'group-list',
    component: GroupListAdminComponent,
    loadChildren: () => import('./routes/group-list/group-list.module').then(m => m.GroupListModule),
    canActivate: [AdminGuard]
  },
  {
    path: 'user-list',
    component: UserListAdminComponent,
    loadChildren: () => import('./routes/user-list/user-list.module').then(m => m.UserListModule),
    canActivate: [AdminGuard]
  },
  {
    path: 'covariate',
    loadChildren: () => import('./routes/covariates/covariates.module').then(m => m.CovariatesModule),
    canActivate: [ModellerGuard]
  },
  {
    path: "geonetwork/:id",
    component: GeonetworkToolComponent
  },
  {
    path: 'admin',
    loadChildren: () => import('./routes/admin/admin.module').then(m => m.AdminModule),
    canActivate: [AdminGuard]
  },
  {
    path: 'location',
    loadChildren: () => import('./routes/location/location.module').then(m => m.LocationModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'import',
    component: ImportComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'export',
    component: ExportComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'not-found',
    component: NotFoundComponent
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent
  },
  {
    path: '**',
    redirectTo: 'login',
    pathMatch: 'full'
  }
]

@NgModule({
  declarations: [
      AppComponent,
      GeonetworkToolComponent,
    ],
  bootstrap: [AppComponent],
  imports: [
    // Angular Modules
    CommonModule,
    // Feature modules
    ApplicationCoreModule,
    HeaderModule,
    MenuBarModule,
    FooterModule,
    TopBarModule,
    LoginModule,
    MatSnackBarModule,
    ImportModule,
    ExportModule,
    RouterModule.forRoot(routes, { enableTracing: false })],
    providers: [
      {
          provide: MAT_DATE_LOCALE,
          useValue: enGB
      },
      {
          provide: DateAdapter,
          useClass: DateFnsAdapter,
          deps: [
              MAT_DATE_LOCALE
          ]
      },
      {
          provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
          useValue: {
              appearance: 'fill'
          }
      },
      {
          provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
          useValue: {
              duration: 4000,
              horizontalPosition: 'end'
          }
      },
      {
          provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
          useValue: {
              showDelay: 500,
              position: 'above'
          }
      },
      provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  constructor(iconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    iconRegistry.addSvgIconSet(
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/mdi.svg')
    );

    iconRegistry.addSvgIcon(
      'ecdc-logo',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/ecdc-logo.svg')
    );

    iconRegistry.addSvgIcon(
      'fao-logo',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/fao-logo.svg')
    );

    iconRegistry.addSvgIcon(
      'geonetwork-logo',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/geonetwork-logo.svg')
    );

    iconRegistry.addSvgIcon(
      'padiweb-logo',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/padiweb-logo.svg')
    );

    iconRegistry.addSvgIcon(
      'woah-logo',
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/images/woah-logo.svg')
    );
  }
}
